/**
 * Campaign handle rules — NGE-14422.
 *
 * `?cmp=` is attacker-controlled: anyone can send a link. Its value used to
 * be interpolated into the GraphQL text that looks the campaign up, and then written — as
 * typed, not as the store returned it — into the `_campaign` cart attribute, which every
 * page prints into a <script>. So the query's shape was the link-sender's to choose, and
 * whatever they typed was what reached the cart.
 *
 * Three rules close that:
 *   1. a URL value that is not a plain handle is ignored before any request (`toHandle`);
 *   2. the handle travels as a GraphQL VARIABLE, never as query text (`campaignRequest`);
 *   3. what is stored is the handle the metaobject returned (CampaignsAndChannels#formatData).
 *
 * `.mjs` and imported by path, not through the `@utils` barrel, for the reason given at
 * the top of campaigns-and-channels.js (NGE-10856): it is inlined into that versioned entry.
 */

/**
 * A CAMPAIGN handle: starts with a lowercase letter or digit, then letters, digits, `-` and
 * `_`, capped at Shopify's 255-character handle limit. Every live `cm_campaign` handle on
 * STIT and PTFR matched on 2026-10-07. Not the channel rule — `?ch=` is deliberately kept out
 * of this (see CampaignsAndChannels#isNewChannel).
 */
const HANDLE = /^[a-z0-9][a-z0-9_-]{0,254}$/;

/**
 * Normalise a URL / stored value to a handle, or reject it. Case-insensitive (the handle is
 * lowercased, as the campaigns app stores it); nothing is trimmed — a padded value never
 * matched a metaobject before either.
 * @param {unknown} value
 * @returns {string|null} The lowercased handle, or null when it is not a plain handle
 */
export function toHandle(value) {
  if (typeof value !== 'string') return null;
  const handle = value.toLowerCase();
  return HANDLE.test(handle) ? handle : null;
}

export const CAMPAIGN_QUERY = `
  query GetCampaign($handle: String!, $type: String!) {
    metaobject(handle: { handle: $handle, type: $type }) {
      id
      handle
      title: field(key: "title") {
        value
      }
      start_time: field(key: "start_time") {
        value
      }
      end_time: field(key: "end_time") {
        value
      }
      active: field(key: "active") {
        value
      }
      channels: field(key: "channels") {
        value
      }
      market_groups: field(key: "market_groups") {
        value
      }
    }
  }
`;

/**
 * The Storefront API request body for one campaign lookup.
 * @param {unknown} handle - The campaign handle, validated here
 * @param {string} scope - The app scope prefix, e.g. `app--123--`
 * @returns {{query: string, variables: {handle: string, type: string}}|null} null for a non-handle
 */
export function campaignRequest(handle, scope) {
  const clean = toHandle(handle);
  if (!clean) return null;
  return { query: CAMPAIGN_QUERY, variables: { handle: clean, type: `${scope || ''}cm_campaign` } };
}
